import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  FileText,
  Upload,
  CheckCircle,
  ArrowRight,
  Loader
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const BookConsultationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Service info (from URL or selection)
    serviceName: searchParams.get('service') || '',
    divisionId: searchParams.get('division') || '',
    
    // Consultation type
    consultationType: '',
    
    // Lawyer selection
    lawyerId: 'auto',
    
    // Date and time
    selectedDate: '',
    selectedTime: '',
    
    // Client info
    clientName: '',
    phone: '',
    email: '',
    clientType: 'individual',
    notes: '',
    
    // Documents (optional)
    documents: []
  });

  const allServices = [
    { id: 'litigation', nameAr: 'دائرة التقاضي' },
    { id: 'consultation', nameAr: 'دائرة الدراسات والاستشارات' },
    { id: 'contracts', nameAr: 'دائرة العقود والشركات' },
    { id: 'notarization', nameAr: 'دائرة التوثيق' },
    { id: 'collection', nameAr: 'دائرة التحصيل والتسويات الودية' },
    { id: 'cybercrime', nameAr: 'دائرة الجرائم المعلوماتية' },
    { id: 'compliance', nameAr: 'دائرة الامتثال والحوكمة' },
    { id: 'ip', nameAr: 'دائرة الملكية الفكرية' },
    { id: 'franchise', nameAr: 'دائرة الامتياز التجاري' }
  ];

  const consultationTypes = [
    { id: 'initial', nameAr: 'استشارة مبدئية', descAr: 'استشارة أولية لفهم الحالة وتقديم التوجيه الأساسي' },
    { id: 'detailed', nameAr: 'استشارة تفصيلية', descAr: 'استشارة شاملة ومفصلة مع دراسة الحالة بشكل كامل' },
    { id: 'quote', nameAr: 'طلب عرض سعر', descAr: 'الحصول على عرض سعر مفصل للخدمة المطلوبة' },
    { id: 'follow-up', nameAr: 'متابعة قضية قائمة', descAr: 'متابعة قضية أو طلب سابق' }
  ];

  const mockLawyers = [
    { id: 'auto', nameAr: 'أفضل محامٍ متاح', specialty: 'تلقائي' },
    { id: '1', nameAr: 'المستشار أحمد العتيبي', specialty: 'التقاضي والقضايا التجارية' },
    { id: '2', nameAr: 'المستشارة سارة المالكي', specialty: 'العقود والشركات' },
    { id: '3', nameAr: 'المحامي خالد الشمري', specialty: 'الجرائم المعلوماتية' },
    { id: '4', nameAr: 'المحامية نورة القحطاني', specialty: 'الملكية الفكرية' }
  ];

  // Generate available time slots
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      documents: files
    }));
  };

  const handleNext = () => {
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Create booking data
      const bookingData = {
        ...formData,
        bookingDate: new Date().toISOString(),
        status: 'pending'
      };

      const response = await fetch(`${backendUrl}/api/consultations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
            step >= s ? 'bg-wazin-gold text-wazin-navy' : 'bg-gray-200 text-gray-500'
          }`}>
            {s}
          </div>
          {s < 4 && (
            <div className={`w-16 h-1 ${step > s ? 'bg-wazin-gold' : 'bg-gray-200'}`}></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">الخطوة 1: اختيار الخدمة</h2>
      
      {/* Service Name (if pre-selected) */}
      {formData.serviceName && (
        <div className="bg-wazin-gold-50 border-2 border-wazin-gold rounded-xl p-4">
          <p className="text-sm text-gray-600 mb-1">الخدمة المختارة:</p>
          <p className="text-lg font-bold text-wazin-navy">{formData.serviceName}</p>
          <p className="text-sm text-gray-500 mt-1">
            {allServices.find(s => s.id === formData.divisionId)?.nameAr}
          </p>
        </div>
      )}

      {/* Service Selection (if not pre-selected) */}
      {!formData.serviceName && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            اختر الخدمة <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.serviceName}
            onChange={(e) => handleChange('serviceName', e.target.value)}
            placeholder="أدخل اسم الخدمة أو وصف احتياجك"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
            required
          />
        </div>
      )}

      {/* Consultation Type */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          نوع الاستشارة / الطلب <span className="text-red-500">*</span>
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          {consultationTypes.map((type) => (
            <div
              key={type.id}
              onClick={() => handleChange('consultationType', type.id)}
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                formData.consultationType === type.id
                  ? 'border-wazin-gold bg-wazin-gold-50'
                  : 'border-gray-200 hover:border-amber-300'
              }`}
            >
              <h3 className="font-bold text-gray-900 mb-1">{type.nameAr}</h3>
              <p className="text-sm text-gray-600">{type.descAr}</p>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={handleNext}
        disabled={!formData.consultationType}
        className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 ${
          formData.consultationType
            ? 'bg-gradient-to-r from-wazin-gold to-wazin-gold-600 hover:shadow-lg'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
      >
        التالي
        <ArrowRight size={20} />
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">الخطوة 2: اختيار المحامي</h2>

      <div className="space-y-3">
        {mockLawyers.map((lawyer) => (
          <div
            key={lawyer.id}
            onClick={() => handleChange('lawyerId', lawyer.id)}
            className={`p-4 border-2 rounded-xl cursor-pointer transition-all flex items-center gap-4 ${
              formData.lawyerId === lawyer.id
                ? 'border-amber-500 bg-amber-50'
                : 'border-gray-200 hover:border-amber-300'
            }`}
          >
            <div className="w-12 h-12 bg-gradient-to-br from-wazin-gold to-wazin-gold-600 rounded-full flex items-center justify-center text-wazin-navy font-bold text-xl">
              {lawyer.nameAr.charAt(0)}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">{lawyer.nameAr}</h3>
              <p className="text-sm text-gray-600">{lawyer.specialty}</p>
            </div>
            {formData.lawyerId === lawyer.id && (
              <CheckCircle className="text-wazin-gold" size={24} />
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-4">
        <button
          onClick={handleBack}
          className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:border-gray-400"
        >
          رجوع
        </button>
        <button
          onClick={handleNext}
          className="flex-1 py-3 bg-gradient-to-r from-wazin-gold to-wazin-gold-600 text-wazin-navy rounded-xl font-bold hover:shadow-lg"
        >
          التالي
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">الخطوة 3: اختيار الموعد</h2>

      {/* Date Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          التاريخ <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={formData.selectedDate}
          onChange={(e) => handleChange('selectedDate', e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
          required
        />
      </div>

      {/* Time Selection */}
      {formData.selectedDate && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            الوقت المفضل <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {timeSlots.map((time) => (
              <button
                key={time}
                onClick={() => handleChange('selectedTime', time)}
                className={`py-3 px-4 border-2 rounded-xl font-semibold transition-all ${
                  formData.selectedTime === time
                    ? 'border-wazin-gold bg-wazin-gold-50 text-wazin-navy'
                    : 'border-gray-200 text-gray-700 hover:border-amber-300'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={handleBack}
          className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:border-gray-400"
        >
          رجوع
        </button>
        <button
          onClick={handleNext}
          disabled={!formData.selectedDate || !formData.selectedTime}
          className={`flex-1 py-3 rounded-xl font-bold text-white ${
            formData.selectedDate && formData.selectedTime
              ? 'bg-gradient-to-r from-wazin-gold to-wazin-gold-600 hover:shadow-lg'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          التالي
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">الخطوة 4: بياناتك</h2>

      {/* Client Name */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          الاسم / اسم المنشأة <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.clientName}
          onChange={(e) => handleChange('clientName', e.target.value)}
          placeholder="أدخل الاسم الكامل أو اسم الشركة"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
          required
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          رقم الجوال <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          placeholder="05xxxxxxxx"
          pattern="[0-9]{10}"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
          required
        />
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          البريد الإلكتروني <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="example@domain.com"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
          required
        />
      </div>

      {/* Client Type */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          نوع العميل <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.clientType}
          onChange={(e) => handleChange('clientType', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
        >
          <option value="individual">فرد</option>
          <option value="company">شركة</option>
          <option value="entity">جهة</option>
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          ملاحظات / وصف مختصر للحالة
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows="4"
          placeholder="اكتب وصفاً موجزاً للحالة أو الاستشارة المطلوبة..."
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none resize-none"
        />
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          رفع مستندات (اختياري)
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-amber-500 transition-all">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Upload className="mx-auto text-gray-400 mb-2" size={32} />
            <p className="text-sm text-gray-600">اضغط لرفع الملفات</p>
            <p className="text-xs text-gray-400 mt-1">PDF, DOC, JPG, PNG (حتى 10MB)</p>
          </label>
          {formData.documents.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-green-600 font-semibold">
                تم رفع {formData.documents.length} ملف
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Submit Status */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-xl">
          <p className="font-semibold flex items-center gap-2">
            <CheckCircle size={20} />
            تم تأكيد الحجز بنجاح!
          </p>
          <p className="text-sm mt-1">سيتم التواصل معك قريباً لتأكيد الموعد.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-xl">
          <p className="font-semibold">حدث خطأ في الحجز</p>
          <p className="text-sm mt-1">الرجاء المحاولة مرة أخرى أو التواصل معنا مباشرة.</p>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleBack}
          className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:border-gray-400"
        >
          رجوع
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.clientName || !formData.phone || !formData.email}
          className={`flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 ${
            !isSubmitting && formData.clientName && formData.phone && formData.email
              ? 'bg-gradient-to-r from-wazin-gold to-wazin-gold-600 hover:shadow-lg'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader className="animate-spin" size={20} />
              جاري الحجز...
            </>
          ) : (
            <>
              تأكيد الحجز
              <CheckCircle size={20} />
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-wazin-navy to-wazin-navy-dark text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-center text-white">
            احجز استشارتك القانونية
          </h1>
          <p className="text-center text-wazin-gold-100">
            نظام الحجز الذكي - خطوات بسيطة للحصول على استشارة قانونية احترافية
          </p>
        </div>
      </div>

      {/* Booking Form */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8">
          {renderStepIndicator()}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
      </div>
    </div>
  );
};

export default BookConsultationPage;
